import {Spinner, Tab, Icon, SpinnerSize} from "@blueprintjs/core";
import { useApi, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useParams, useHistory } from "react-router";
import {
  PedigreeRequestApi,
  RequestApi,
  ERequestType,
  ERequestState,
  ExpatriationRequestApi,
  BooleanDto,
} from "../../../../api";
import {
  PageBase,
  RequestStickyMenu,
  ProtectedTabs,
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  RequestHistoryTab,
  VerificationTab,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { PedigreeRequestDetailPage } from "../../pedigreeRequest/item/PedigreeRequestDetailPage";
import { ExpatriationRequestDetailPanel } from "./ExpatriationRequestDetailPanel";

export interface IExpatriationRequestItemPageProps {}

export const ExpatriationRequestItemPage: React.FunctionComponent<IExpatriationRequestItemPageProps> = (props) => {
  const { id, tab, state } = useParams<{
    id: string;
    tab: string;
    state: string;
  }>();
  const history = useHistory();
  const requestApi = useApiService(RequestApi);
  const expatriationApi = useApiService(ExpatriationRequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.ExpatriationRequest, +id, 0, currentLanguage.languageId),
    [requestApi, id, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(async () => {
    await expatriationApi.closeExpatriationRequest(requestInfo?.parentRequestId);
  }, [expatriationApi, requestInfo?.parentRequestId]);

  const [isForeignDog, isForeignDogLoading, ,] = useApiEffect(
    () => (+id > 0 ? expatriationApi.getExpatriationRequestIsForeignDog(+id) : BooleanDto.fromJS({ value: true })),
    [requestApi, id, currentLanguage],
  );

  //   const closeRequest = React.useCallback(async () => {
  //     await expatriationApi.closeExpatriationrequest(requestInfo?.parentRequestId);
  //   }, [api, requestInfo]);

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.ExpatriationRequest),
          route: ERoutes.expatriationRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryExpatriationRequest,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (+id > 0 && !requestInfoLoading) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsExpatriationRequest"
        onChange={(newTabId) => history.push(`${ERoutes.expatriationRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.expatriationRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<ExpatriationRequestDetailPanel expatriationRequestId={+id} editMode={state === "edit"} />}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <ConditionTab
                requestId={+requestInfo.parentRequestId}
                editMode={state === "edit"}
                requestState={ERequestState.REQUESTED}
                statusETLCode={ETLCodes.ConditionsForBillingStatus}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="verification"
          title={t(ETLCodes.Verifications)}
          disabled={+id <= 0 || isForeignDog?.value}
          panel={
            requestInfo ? (
              <VerificationTab
                requestId={requestInfo?.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            ) : null
          }
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
